// frontend/src/pages/LessonDetail.tsx
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import authAxios from '../auth/authAxios';
import { Target, ListOrdered, Lightbulb, Sparkles, PencilLine, Send, ArrowLeftCircle } from 'lucide-react';

import { useScoreContext } from '../ScoreContext';

interface Lesson {
  lesson_id: string;
  title: string;
  description: string;
  completed: boolean;
  objective?: string;
  steps?: string;
  hint?: string;
  example_prompt?: string;
  max_score?: number;
}

interface Challenge {
  challenge_id: string
  title: string
  description: string
  badge_icon: string
  passing_score: number
  lessons: Lesson[]
}


interface Feedback {
  score: number;
  feedback: string;
  updated_score: number;
}

const LessonDetail = () => {
  const { challenge_id, lesson_id } = useParams();
  const navigate = useNavigate();

  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [prompt, setPrompt] = useState('');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const { triggerRefresh } = useScoreContext();

  useEffect(() => {
    if (challenge_id) {
      // Fetch challenge with lessons
      authAxios.get(`/api/challenges/${challenge_id}`)
        .then(res => {
          setChallenge(res.data);
          // Find lesson index and check locked status
          const idx = res.data.lessons.findIndex((l: { lesson_id: string | undefined; }) => l.lesson_id === lesson_id);
          setCurrentIndex(idx);
          if (idx > 0 && !res.data.lessons[idx - 1].completed) {
            setIsLocked(true);
          } else {
            setIsLocked(false);
          }
        });
    }

    // Always fetch lesson details
    authAxios.get(`/api/lessons/${lesson_id}`)
      .then(res => setLesson(res.data));
  }, [challenge_id, lesson_id]);

  const goPrev = () => {
    console.info("navigate");
    if (!challenge_id) return;
    navigate(`/challenges/${challenge_id}`);
  };

  useEffect(() => {
    if (feedback) {
      setLesson((prev) =>
        prev ? { ...prev, completed: true } : prev
      );
    }
  }, [feedback]);

  const handleEvaluate = async () => {
    if (!lesson) return;
    setIsEvaluating(true);

    try {
      const res = await authAxios.post('/api/lessons/evaluate', {
        prompt,
        lesson_id: lesson.lesson_id,
      });
      setFeedback(res.data);
      triggerRefresh();
    } catch (err) {
      console.error('Evaluation failed:', err);
    } finally {
      setIsEvaluating(false);
    }
  };

  if (!lesson) return <p className="p-6">Loading lesson...</p>;

  return (
    <main className="min-w-[50vw] max-w-[800px] mx-auto p-6">
      {feedback && (
        <div className="mt-6 rounded-xl shadow border bg-bg-panel bg-opacity-90 backdrop-blur-sm border border-white/10 shadow-[0_0_10px_rgba(79,255,222,0.1)] p-6">
          <h4 className="text-lg font-semibold  mb-1 text-primary flex items-center gap-2"><Sparkles className="w-5 h-5" />Feedback</h4>
          <p className="text-sm  mb-2">{feedback.feedback}</p>
          <p className="text-sm text-green-700">Score: {feedback.score.toFixed(1)}</p>
        </div>
      )}
      <>
        <div className="mt-6 rounded-xl shadow border bg-bg-panel bg-opacity-90 backdrop-blur-sm border border-white/10 shadow-[0_0_10px_rgba(79,255,222,0.1)] p-6">
          {lesson.objective && (
            <div className='mb-6'>
              <h4 className="text-lg font-semibold mb-1 text-primary flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Objective
              </h4>
              <p className="text-sm  mb-3">{lesson.objective}</p>
            </div>
          )}

          {lesson.steps && (
            <div className='mb-6'>
              <h4 className="text-lg font-semibold mb-1 text-primary flex items-center gap-2">
                <ListOrdered className="w-5 h-5 text-primary" />
                Steps
              </h4>
              {lesson.steps.split('\n').map((step, idx) => (
                <p key={idx} className="text-sm  mb-1">{step}</p>
              ))}
            </div>
          )}

          {lesson.hint && (
            <div className='mb-6'>
              <h4 className="text-lg font-semibold mt-4 mb-1 text-yellow-400 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
                Hint
              </h4>
              <p className="text-sm text-yellow-500">{lesson.hint}</p>
            </div>
          )}

          <div className='mt-6 mb-6'>
            <h4 className="text-lg font-semibold mt-4 mb-1 flex items-center gap-2">
              <PencilLine className="w-4 h-4 text-white" />
              Write your prompt
            </h4>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
              className="form-glass w-full p-3 focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
              placeholder={lesson.example_prompt} />
          </div>
          <div className="flex gap-3 mt-3">
            <button
              disabled={isEvaluating}
              onClick={handleEvaluate}
              className={`mt-3 px-5 py-2 rounded-lg shadow flex items-center gap-2 transition
                  ${isEvaluating
                  ? 'bg-button/60 cursor-wait'
                  : 'bg-button hover:bg-button-highlight text-white'}
                `}
            >
              {isEvaluating ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12" cy="12" r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Evaluating...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Prompt
                </>
              )}
            </button>

            {challenge && (
              <button
                className="mt-3 px-5 py-2 rounded-lg shadow flex items-center gap-2 transition bg-button hover:bg-button-highlight text-white"
                onClick={goPrev}
              >
                <ArrowLeftCircle className="w-4 h-4" />
                Return to Challenge
              </button>
            )}
          </div>
        </div>
      </>
    </main>
  );
};

export default LessonDetail;
